import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { formatXP } from '@/utils/formatXP';
import { Badge } from '@/components/atoms/Badge';
import styles from './RankingItem.module.scss';
import type { RankingItemProps } from './RankingItem.types';

/**
 * RankingItem - Component for displaying user ranking
 * Composition: Rank + Name + XP
 *
 * @example
 * <RankingItem
 *   rank={1}
 *   name="John Doe"
 *   xp={1250}
 *   isCurrentUser={true}
 * />
 */
export function RankingItem({
  rank,
  name,
  xp,
  isCurrentUser = false,
  onClick,
  className,
  ...props
}: RankingItemProps) {
  const { t } = useTranslation();
  const itemClasses = cn(
    styles.rankingItem,
    isCurrentUser && styles['rankingItem--current'],
    onClick && styles['rankingItem--clickable'],
    className
  );

  const rankClasses = cn(
    styles.rankingItem__rank,
    rank === 1 && styles['rankingItem__rank--top1'],
    rank === 2 && styles['rankingItem__rank--top2'],
    rank === 3 && styles['rankingItem__rank--top3']
  );

  const getRankDisplay = () => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return rank;
  };

  return (
    // TODO accesibilidad: convertir a <button> o añadir role/keyboard. Pre-existente.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className={itemClasses} onClick={onClick} {...props}>
      {/* Rank number or medal */}
      <div className={rankClasses}>{getRankDisplay()}</div>

      {/* User info */}
      <div className={styles.rankingItem__info}>
        <span className={styles.rankingItem__name}>
          {name}
          {isCurrentUser && <span className={styles.rankingItem__you}>{t('rankingItem.you')}</span>}
        </span>
      </div>

      {/* XP badge */}
      <div className={styles.rankingItem__xp}>
        <Badge
          variant={isCurrentUser ? 'secondary' : 'default'}
          size="sm"
          className={isCurrentUser ? styles.rankingItem__xpBadgeCurrent : undefined}
        >
          {formatXP(xp)} {t('rankingItem.xp')}
        </Badge>
      </div>
    </div>
  );
}

RankingItem.displayName = 'RankingItem';
